// Thin async wrapper over citeproc-js (loaded lazily via cslProcessor).
//
// As of Day 4 of the CSL repository rollout, `CitationStyle` is a free
// `string` (a canonical CSL style id from `csl_styles_catalog`). The old
// three-letter codes (`chicago-nb`, `mla`, `apa`) are still tolerated on
// read via LEGACY_ALIAS so saved profiles and `metadata.citationStyle`
// blobs keep rendering until they're naturally overwritten by the new
// picker.
//
// Public surface:
//   • renderCitation(csl, opts)                  → Promise<RenderedCitation>
//   • renderCitationSequence(style, occurrences) → Promise<RenderedCitation[]>
//   • resolveCitationStyle / resolveCslStyleId
//   • CITATION_STYLES (well-known seed ids — quick-pick list for the picker)
//   • CITATION_STYLE_LABELS + getCitationStyleLabel(id, catalogTitle?)
//   • formatConsultedOmissionAlert
//
// All formatting is produced by citeproc against the official CSL XML
// streamed from the `csl-styles` private bucket.
import { renderBibliography, renderCitations, getStyleClass } from "@/lib/cslProcessor";
import type { CslItem } from "@/lib/citations";

/**
 * A canonical CSL style id (e.g. "chicago-notes-bibliography", "apa",
 * "modern-language-association"). Stored verbatim in
 * `profiles.default_citation_style` and `metadata.citationStyle`.
 *
 * The constraint is just shape — `^[a-z0-9-]+$` — and we trust the
 * picker to only emit ids that exist in `csl_styles_catalog`.
 */
export type CitationStyle = string;

export type CitationMode = "footnote" | "short-note" | "bibliography";

export type RenderOptions = {
  style: CitationStyle;
  mode: CitationMode;
  /** Optional locator (page, paragraph) appended in footnote/short-note modes. */
  locator?: string | null;
};

export type RenderedCitation = {
  text: string;
  html: string;
};

/**
 * Old UI codes → new canonical CSL ids. Kept indefinitely so any value
 * persisted before Day 4 keeps rendering. The picker only ever writes
 * canonical ids, so this table never grows.
 */
const LEGACY_ALIAS: Record<string, string> = {
  "chicago-nb": "chicago-notes-bibliography",
  mla: "modern-language-association",
  apa: "apa",
};

const STYLE_ID_RE = /^[a-z0-9][a-z0-9-]{0,127}$/i;

/** Normalize any stored or user-supplied style id to its canonical form. */
export const resolveCslStyleId = (id: string): string =>
  LEGACY_ALIAS[id] ?? id;

/** Default that ships when nothing else is set. */
export const DEFAULT_CITATION_STYLE = "chicago-notes-bibliography";

// citeproc emits HTML for both inline citations and bibliography entries.
// Our consumers want a plain-text twin for clipboard / aria-label uses.
const stripHtml = (html: string): string =>
  html
    .replace(/<[^>]+>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&ldquo;/g, "\u201C")
    .replace(/&rdquo;/g, "\u201D")
    .replace(/\s+/g, " ")
    .trim();

const itemId = (csl: CslItem): string =>
  String(csl.id ?? `tmp-${Math.random().toString(36).slice(2)}`);

/**
 * Render a single citation. For "bibliography" mode this returns the
 * bibliography entry for the item; for "footnote" / "short-note" it
 * returns a one-cluster inline citation. The cluster mode (note vs
 * in-text) is taken from the style's CSL `class` attribute, so any
 * note-style produces footnote-shaped output and any in-text style
 * produces parenthetical output — automatically, across all ~3k styles.
 */
export const renderCitation = async (
  csl: CslItem,
  options: RenderOptions,
): Promise<RenderedCitation> => {
  const { style, mode, locator } = options;
  const styleId = resolveCslStyleId(style);
  const id = itemId(csl);

  if (mode === "bibliography") {
    const { entries } = await renderBibliography(styleId, [{ ...csl, id }]);
    const html = entries[0] ?? "";
    return { text: stripHtml(html), html };
  }

  const clusterMode = await getStyleClass(styleId);
  const itemsById = { [id]: { ...csl, id } };
  const [html] = await renderCitations(
    styleId,
    itemsById,
    [{ items: [{ id, ...(locator ? { locator } : {}) }], mode: clusterMode }],
  );
  const safeHtml = html ?? "";
  return { text: stripHtml(safeHtml), html: safeHtml };
};

export type FootnoteOccurrence = {
  csl: CslItem;
  locator?: string | null;
};

/**
 * Render an ordered sequence of inline citations as one engine session,
 * so citeproc applies the active style's first-vs-subsequent + Ibid rules.
 * Returns one `RenderedCitation` per input occurrence in order.
 */
export const renderCitationSequence = async (
  style: CitationStyle,
  occurrences: FootnoteOccurrence[],
): Promise<RenderedCitation[]> => {
  if (occurrences.length === 0) return [];
  const styleId = resolveCslStyleId(style);
  const clusterMode = await getStyleClass(styleId);

  const itemsById: Record<string, CslItem> = {};
  const clusters = occurrences.map((occ) => {
    const id = itemId(occ.csl);
    itemsById[id] = { ...occ.csl, id };
    return {
      items: [{ id, ...(occ.locator ? { locator: occ.locator } : {}) }],
      mode: clusterMode,
    };
  });

  const htmls = await renderCitations(styleId, itemsById, clusters);
  return htmls.map((html) => ({ text: stripHtml(html ?? ""), html: html ?? "" }));
};

/**
 * Convenience: does this style render as numbered notes (Chicago NB,
 * Turabian, etc.)? Pure heuristic on the id — used by UI hints that
 * shouldn't pay the cost of a network round-trip just to render a label.
 */
export const isNoteStyleHeuristic = (style: CitationStyle): boolean => {
  const id = resolveCslStyleId(style);
  return /(^|-)(note|notes)(-|$)/.test(id) || id === "chicago-notes-bibliography";
};

/**
 * Well-known starting points surfaced in the picker's "Popular" group
 * and used as label-map keys. The full ~3k catalog lives in
 * `csl_styles_catalog`; this is just the curated shortlist.
 */
export const CITATION_STYLES: CitationStyle[] = [
  "chicago-notes-bibliography",
  "chicago-author-date",
  "modern-language-association",
  "apa",
  "harvard-cite-them-right",
  "ieee",
  "american-medical-association",
  "vancouver-nlm",
  "nature",
];

/** Short labels for the seed ids above. */
export const CITATION_STYLE_LABELS: Record<string, string> = {
  "chicago-notes-bibliography": "Chicago (Notes-Bibliography)",
  "chicago-author-date": "Chicago (Author-Date)",
  "modern-language-association": "MLA",
  apa: "APA",
  "harvard-cite-them-right": "Harvard (Cite Them Right)",
  ieee: "IEEE",
  "american-medical-association": "AMA",
  "vancouver-nlm": "Vancouver (NLM)",
  nature: "Nature",
  // Legacy aliases — shown if anyone still has them saved before the picker overwrites.
  "chicago-nb": "Chicago (Notes-Bibliography)",
  mla: "MLA",
};

/** Human-readable label for any style id; falls back to the catalog title or the humanized id. */
export const getCitationStyleLabel = (
  id: string,
  catalogTitle?: string | null,
): string => {
  if (CITATION_STYLE_LABELS[id]) return CITATION_STYLE_LABELS[id];
  const canonical = resolveCslStyleId(id);
  if (CITATION_STYLE_LABELS[canonical]) return CITATION_STYLE_LABELS[canonical];
  if (catalogTitle && catalogTitle.trim()) return catalogTitle.trim();
  // Humanize an id like "harvard-cite-them-right" → "Harvard cite them right".
  return canonical
    .split("-")
    .map((p, i) => (i === 0 ? p.charAt(0).toUpperCase() + p.slice(1) : p))
    .join(" ");
};

/**
 * Resolve which citation style applies for a given context. Cascade:
 *   per-publication override → workspace default → DEFAULT_CITATION_STYLE.
 * Any non-empty string that matches `^[a-z0-9-]+$` is accepted at either
 * layer; the picker enforces "exists in catalog" at write time.
 */
export const resolveCitationStyle = (
  workspaceDefault: string | null | undefined,
  publicationOverride: string | null | undefined,
): CitationStyle => {
  const valid = (v: string | null | undefined): v is string =>
    !!v && STYLE_ID_RE.test(v);
  if (valid(publicationOverride)) return resolveCslStyleId(publicationOverride);
  if (valid(workspaceDefault)) return resolveCslStyleId(workspaceDefault);
  return DEFAULT_CITATION_STYLE;
};

/**
 * "Consulted sources omitted" alert: shown in CitationsPreviewPanel
 * when the active style is anything other than Chicago Notes-Bibliography
 * and the Piece has at least one consulted-but-not-cited reference.
 */
export const formatConsultedOmissionAlert = (
  style: CitationStyle,
  consultedCount: number,
): string | null => {
  if (resolveCslStyleId(style) === "chicago-notes-bibliography") return null;
  if (consultedCount <= 0) return null;
  const plural = consultedCount === 1 ? "" : "s";
  return (
    `${getCitationStyleLabel(style)} bibliographies list cited works only — ` +
    `the ${consultedCount} consulted source${plural} won't appear here. ` +
    `Switch to Chicago Notes-Bibliography to include them.`
  );
};
